import { FIELDS } from 'modules/cm/fields/types';
import { CaseData } from 'modules/cm/api/cases/types';
import { ReadonlyFields } from 'modules/cm/domain/Case/ReadonlyFields';
import { CaseDetails } from 'modules/cm/pages/CaseDetailsPage/CaseDetailsPage.types';
import { format } from 'utils/date';
import { notEmpty } from 'utils/types';
import { FIELDS as CURRENT_INCOME_FIELDS } from 'modules/cm/domain/Case/Applicant/Income/Current/Current';
import { FIELDS as FUTURE_INCOME_FIELDS } from 'modules/cm/domain/Case/Applicant/Income/Future/Future';

type ApplicantKey = 'applicant1' | 'applicant2';
type RawApplicant = NonNullable<CaseData[ApplicantKey]>;

export const mapPepperUploadDate = (apiValue: CaseData['pepperUploadDate']) => {
  return apiValue ? format(apiValue).dataTimeWithHoursAndMinutes() : apiValue;
};

export const toReadonlyFields = (api: CaseData): ReadonlyFields => {
  type ApplicantRelated = NonNullable<CaseDetails['readonlyFields']>['applicants'][0];

  const mapApplicant = (applicantApi: RawApplicant): ApplicantRelated => {
    return {
      [FIELDS.APPLICANT_AGE]: applicantApi.age,
      [FIELDS.COMMITMENT_TO_BE_CLEARED]: {
        [FIELDS.BALANCE]: applicantApi.commitmentsBalanceToBeCleared,
        [FIELDS.MONTHLY_AMOUNT]: applicantApi.commitmentsMonthlyAmountToBeCleared,
      },
      [FIELDS.COMMITMENT_REMAINING]: {
        [FIELDS.BALANCE]: applicantApi.commitmentsBalanceRemaining,
        [FIELDS.MONTHLY_AMOUNT]: applicantApi.commitmentsMonthlyAmountRemaining,
      },
      currentIncome: {
        [CURRENT_INCOME_FIELDS.EMPLOYED_TOTAL]: applicantApi.currentIncomeEmployedTotal,
        [CURRENT_INCOME_FIELDS.SELF_EMPLOYED_TOTAL]: applicantApi.currentIncomeSelfEmployedTotal,
        [CURRENT_INCOME_FIELDS.OTHER_TOTAL]: applicantApi.currentIncomeOtherTotal,
        [CURRENT_INCOME_FIELDS.CURRENT_INCOME_PENSION]: applicantApi.currentIncomePensionIncome,
        [CURRENT_INCOME_FIELDS.TOTAL]: applicantApi.currentIncomeTotal,
      },
      futureIncome: {
        [FUTURE_INCOME_FIELDS.MINIMUM_RETIRED_INCOME]: applicantApi.minimumRetiredIncome,
        [FUTURE_INCOME_FIELDS.FUTURE_INCOME_PENSION]: applicantApi.futureIncomePensionIncome,
        [FUTURE_INCOME_FIELDS.FUTURE_INCOME_TOTAL]: applicantApi.futureIncomeTotalIncome,
      },
    };
  };

  return {
    [FIELDS.APPLICANTS]: [api.applicant1, api.applicant2].filter(notEmpty).map(mapApplicant),
    [FIELDS.ADMIN_PREFIX]: {
      [FIELDS.ADMIN_EXPECTED_MATURITY]: api.expectedMaturity,
      [FIELDS.ADMIN_PEPPER_UPLOAD_DATE]: mapPepperUploadDate(api.pepperUploadDate),
    },
    [FIELDS.APPLICATION_DATE]: api.dateSubmitted ? format(api.dateSubmitted).fromBackendToView() : api.dateSubmitted,
    [FIELDS.CASE_ID]: api.caseId,
    [FIELDS.NUMBER_OF_APPLICANTS]: api.numberOfApplicants,
    [FIELDS.DEBT_TO_INCOME]: api.debtToIncome,
    [FIELDS.LTV]: api.ltv,
    [FIELDS.VALUATION_FEE_AMOUNT]: api.valuationFeeAmount,
    [FIELDS.PRODUCT_FIXED_TERM]: api.productFixedTerm,
    [FIELDS.PRODUCT_FEE]: api.productFee,
    [FIELDS.PRODUCT_FIXED_INTEREST_RATE]: api.productFixedInterestRate,
    [FIELDS.PRODUCT_REVERSION_RATE]: api.productReversionRate,
    [FIELDS.GROSS_LOAN]: api.grossLoan,
    [FIELDS.UPFRONT_COMMISSION_AMOUNT]: api.upfrontCommissionAmount,
    [FIELDS.ONGOING_COMMISSION_AMOUNT]: api.ongoingCommissionAmount,
    [FIELDS.NETWORK_NAME]: api.networkName,
    [FIELDS.CLUB_NAME]: api.clubName,
    [FIELDS.SUBMISSION_ROUTE]: api.submissionRoute,
    [FIELDS.COMMISSION_PAID_TO]: api.commissionPaidTo,
  };
};
